import { useState, useMemo, Fragment } from "react";
import { Search, Filter, Library, RefreshCw, Download, ChevronDown, ChevronRight, BookmarkPlus, Check, Loader2, ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { exportResultsCsv } from "@/lib/csv-export";
import { cnFromUrl } from "@/lib/account";

// Collapse duplicate uploaded titles that resolve to the same catalog record
// (or the same found title/author), so each title appears only once.
function dedupeResults(list) {
  const seen = new Set();
  const out = [];
  for (const r of list) {
    const cn = cnFromUrl(r.catalogUrl);
    const key = cn
      ? `cn:${cn}`
      : `t:${(r.foundTitle || r.title || "").toLowerCase().trim()}|${(r.foundAuthor || r.author || "").toLowerCase().trim()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

const bestAvail = (r) => Math.max(r.beatleyAvailable || 0, r.systemAvailable || 0);

// Not-found rows always sort to the bottom; then apply the chosen order.
function sortComparator(sortBy) {
  const byTitle = (a, b) => (a.foundTitle || a.title || "").localeCompare(b.foundTitle || b.title || "");
  return (a, b) => {
    if (!!a.found !== !!b.found) return a.found ? -1 : 1;
    if (sortBy === "author") {
      const c = (a.foundAuthor || a.author || "").localeCompare(b.foundAuthor || b.author || "");
      return c !== 0 ? c : byTitle(a, b);
    }
    if (sortBy === "available") {
      const c = bestAvail(b) - bestAvail(a);
      return c !== 0 ? c : byTitle(a, b);
    }
    if (sortBy === "waiting") {
      const c = (a.holds || 0) - (b.holds || 0);
      return c !== 0 ? c : byTitle(a, b);
    }
    return byTitle(a, b); // "title"
  };
}

const FORMAT_LABELS = { largeprint: "Large Print", book: "Print", ebook: "Ebook" };

function HoldButton({ catalogUrl, onPlaceHold, testid }) {
  const [loading, setLoading] = useState(false);
  const [held, setHeld] = useState(false);

  const handleClick = async (e) => {
    e.stopPropagation();
    if (loading || held) return;
    setLoading(true);
    const ok = await onPlaceHold(catalogUrl);
    setLoading(false);
    if (ok) setHeld(true);
  };

  let content;
  if (held) {
    content = <><Check className="w-3 h-3" /> On hold</>;
  } else if (loading) {
    content = <><Loader2 className="w-3 h-3 animate-spin" /> Placing…</>;
  } else {
    content = <><BookmarkPlus className="w-3 h-3" /> Place Hold</>;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading || held}
      data-testid={testid}
      className="inline-flex items-center gap-1 text-primary hover:underline disabled:no-underline disabled:opacity-70 mt-1"
    >
      {content}
    </button>
  );
}

function formatLabel(fmt) {
  return FORMAT_LABELS[fmt] ?? fmt;
}

function parseSeriesInfo(series) {
  if (!series) return null;
  const m = series.match(/^(.*?)\s*\(Book\s*([\d.]+)\)$/i);
  if (m) return { name: m[1].trim(), position: parseFloat(m[2]) };
  return { name: series.trim(), position: null };
}

function statusBadge(result) {
  if (!result.found) {
    return <Badge variant="secondary" className="bg-muted text-muted-foreground border-transparent text-xs">Not Found</Badge>;
  }
  if (result.beatleyAvailable > 0) {
    return <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 text-xs">Available at Beatley</Badge>;
  }
  if (result.systemAvailable > 0) {
    return <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800 text-xs">Available in System</Badge>;
  }
  if (result.holds > 0) {
    return <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800 text-xs">Holds Only</Badge>;
  }
  return <Badge variant="secondary" className="bg-muted text-muted-foreground border-transparent text-xs">Checked Out</Badge>;
}

function FormatRow({ fmt, isLast, onPlaceHold }) {
  const beatleyStr = fmt.beatleyTotal > 0 ? `${fmt.beatleyAvailable} of ${fmt.beatleyTotal}` : "Not here";
  const sysStr = fmt.systemTotal > 0 ? `${fmt.systemAvailable} of ${fmt.systemTotal}` : "—";
  return (
    <tr className={`bg-muted/20 text-xs ${!isLast ? "border-b border-border/50" : ""}`}>
      <td className="pl-12 pr-4 py-2 text-muted-foreground font-medium">{formatLabel(fmt.formatType)}</td>
      <td className="px-4 py-2">
        <div className="space-y-0.5">
          <div className="flex gap-2">
            <span className="text-muted-foreground w-14 shrink-0">Beatley:</span>
            <span className={fmt.beatleyAvailable > 0 ? "text-primary font-medium" : "text-foreground"}>{beatleyStr}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-muted-foreground w-14 shrink-0">System:</span>
            <span>{sysStr}</span>
          </div>
          {fmt.holds > 0 && (
            <div className="flex gap-2 text-amber-600 dark:text-amber-400">
              <span className="w-14 shrink-0">Waiting:</span>
              <span>{fmt.holds}</span>
            </div>
          )}
        </div>
      </td>
      <td className="px-4 py-2" colSpan={2}>
        <HoldButton catalogUrl={fmt.catalogUrl} onPlaceHold={onPlaceHold} testid={`button-hold-${fmt.formatType}`} />
      </td>
    </tr>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function ResultsSection({ booksToSearch, results, isSearching, progress, checkedAt, onStartSearch, onReset, onPlaceHold }) {
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("title");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());

  const hasStarted = progress > 0 || isSearching || results.length > 0;
  const isComplete = hasStarted && progress === booksToSearch.length && !isSearching;
  const percentage = booksToSearch.length > 0 ? Math.round((progress / booksToSearch.length) * 100) : 0;

  const toggleRow = (idx) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const uniqueResults = useMemo(() => dedupeResults(results), [results]);

  const stats = useMemo(() => {
    let beatley = 0, system = 0, notFound = 0, waiting = 0;
    uniqueResults.forEach((r) => {
      if (!r.found) notFound++;
      else if (r.beatleyAvailable > 0) beatley++;
      else if (r.systemAvailable > 0) system++;
      if (r.found && r.holds > 0) waiting++;
    });
    return { beatley, system, notFound, waiting };
  }, [uniqueResults]);

  const filteredResults = useMemo(() => {
    let filtered = uniqueResults;
    if (filter === "beatley") filtered = filtered.filter((r) => r.found && r.beatleyAvailable > 0);
    else if (filter === "system") filtered = filtered.filter((r) => r.found && r.systemAvailable > 0);
    else if (filter === "largeprint") filtered = filtered.filter((r) => r.found && r.hasLargePrint);
    else if (filter === "ebook") filtered = filtered.filter((r) => r.found && r.hasEbook);
    else if (filter === "not-found") filtered = filtered.filter((r) => !r.found);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((r) => r.title.toLowerCase().includes(q) || r.author.toLowerCase().includes(q));
    }
    return filtered;
  }, [uniqueResults, filter, searchQuery]);

  const displayRows = useMemo(() => {
    if (sortBy !== "series") {
      const sorted = [...filteredResults].sort(sortComparator(sortBy));
      return sorted.map((r) => ({ result: r, seriesName: null, isFirstInGroup: false, groupCount: 1 }));
    }
    // Series mode: cluster titles of the same series, ordered by book number.
    const order = [];
    const groups = new Map();
    let singleCounter = 0;
    for (const r of filteredResults) {
      const info = parseSeriesInfo(r.series);
      const key = info ? `series::${info.name.toLowerCase()}` : `single::${singleCounter++}`;
      if (!groups.has(key)) {
        groups.set(key, { seriesName: info?.name ?? null, items: [] });
        order.push(key);
      }
      groups.get(key).items.push(r);
    }
    for (const key of order) {
      const g = groups.get(key);
      if (g.seriesName) {
        g.items.sort((a, b) => {
          const pa = parseSeriesInfo(a.series)?.position ?? Number.MAX_SAFE_INTEGER;
          const pb = parseSeriesInfo(b.series)?.position ?? Number.MAX_SAFE_INTEGER;
          return pa - pb;
        });
      }
    }
    const flat = [];
    for (const key of order) {
      const g = groups.get(key);
      g.items.forEach((item, i) => {
        flat.push({ result: item, seriesName: g.seriesName, isFirstInGroup: i === 0, groupCount: g.items.length });
      });
    }
    return flat;
  }, [filteredResults, sortBy]);

  return (
    <div className="w-full space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {!hasStarted ? (
        <div className="bg-card border border-border shadow-sm rounded-xl p-8 text-center max-w-xl mx-auto">
          <Library className="w-12 h-12 text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-serif font-bold text-foreground mb-2">
            Ready to check {booksToSearch.length} books
          </h2>
          <p className="text-muted-foreground mb-8">
            Found {booksToSearch.length} books on your "Want to Read" shelf.
            Availability at Beatley Central and across the system will show below for each format.
          </p>
          <div className="flex gap-4 justify-center">
            <Button variant="outline" onClick={onReset} data-testid="button-cancel">Cancel</Button>
            <Button onClick={onStartSearch} size="lg" className="px-8" data-testid="button-check">
              <Search className="w-4 h-4 mr-2" />
              Check Library
            </Button>
          </div>
        </div>
      ) : (
        <>
          {(!isComplete || isSearching) && (
            <div className="bg-card border border-border shadow-sm rounded-xl p-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Checking catalog…</span>
                <span className="text-sm text-muted-foreground">{progress} / {booksToSearch.length} ({percentage}%)</span>
              </div>
              <Progress value={percentage} className="h-2" data-testid="progress-bar" />
            </div>
          )}

          {results.length > 0 && (
            <div className="space-y-6">
              {checkedAt && isComplete && (
                <p className="text-xs text-muted-foreground text-right" data-testid="text-checked-at">
                  Last checked: {formatDate(checkedAt)}
                </p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                  <div className="text-sm text-muted-foreground mb-1">Checked</div>
                  <div className="text-2xl font-bold" data-testid="stat-checked">{uniqueResults.length}</div>
                </div>
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 shadow-sm">
                  <div className="text-sm text-primary mb-1">At Beatley</div>
                  <div className="text-2xl font-bold text-primary" data-testid="stat-beatley">{stats.beatley}</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 shadow-sm">
                  <div className="text-sm text-blue-700 dark:text-blue-400 mb-1">In System</div>
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-400" data-testid="stat-system">{stats.system}</div>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 shadow-sm">
                  <div className="text-sm text-amber-700 dark:text-amber-400 mb-1">Has a Waitlist</div>
                  <div className="text-2xl font-bold text-amber-700 dark:text-amber-400" data-testid="stat-waiting">{stats.waiting}</div>
                </div>
                <div className="bg-muted/50 border border-border rounded-lg p-4 shadow-sm">
                  <div className="text-sm text-muted-foreground mb-1">Not Found</div>
                  <div className="text-2xl font-bold text-muted-foreground" data-testid="stat-notfound">{stats.notFound}</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-between items-end sm:items-center bg-card border border-border p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
                  <Filter className="w-4 h-4 text-muted-foreground" />
                  <Select value={filter} onValueChange={(v) => setFilter(v)}>
                    <SelectTrigger className="w-[170px]" data-testid="select-filter">
                      <SelectValue placeholder="Filter results" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Results</SelectItem>
                      <SelectItem value="beatley">Available at Beatley</SelectItem>
                      <SelectItem value="system">Available in System</SelectItem>
                      <SelectItem value="largeprint">Large Print</SelectItem>
                      <SelectItem value="ebook">E-books</SelectItem>
                      <SelectItem value="not-found">Not Found</SelectItem>
                    </SelectContent>
                  </Select>
                  <ArrowUpDown className="w-4 h-4 text-muted-foreground ml-1" />
                  <Select value={sortBy} onValueChange={(v) => setSortBy(v)}>
                    <SelectTrigger className="w-[170px]" data-testid="select-sort">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="title">Title (A–Z)</SelectItem>
                      <SelectItem value="author">Author (A–Z)</SelectItem>
                      <SelectItem value="available">Most available</SelectItem>
                      <SelectItem value="waiting">Fewest waiting</SelectItem>
                      <SelectItem value="series">Group by series</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div className="relative w-full sm:w-56">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input type="search" placeholder="Search titles…" className="pl-9"
                      value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="input-search" />
                  </div>
                  {isComplete && (
                    <Button variant="outline" size="sm" onClick={() => exportResultsCsv(uniqueResults, checkedAt)}
                      className="shrink-0 gap-1.5" data-testid="button-export">
                      <Download className="h-4 w-4" />
                      Export CSV
                    </Button>
                  )}
                  {isComplete && (
                    <Button variant="outline" size="icon" onClick={onReset} title="Start Over" data-testid="button-reset">
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-muted/30 text-muted-foreground border-b border-border">
                      <tr>
                        <th className="px-6 py-4 font-medium">Book</th>
                        <th className="px-6 py-4 font-medium">Formats</th>
                        <th className="px-6 py-4 font-medium">Best Availability</th>
                        <th className="px-6 py-4 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {displayRows.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-12 text-center text-muted-foreground">
                            No books match the current filters.
                          </td>
                        </tr>
                      ) : (
                        displayRows.map(({ result, seriesName, isFirstInGroup, groupCount }, idx) => {
                          const isExpanded = expandedRows.has(idx);
                          const hasMultipleFormats = (result.formats ?? []).length > 1;
                          return (
                            <Fragment key={`grp-${idx}`}>
                              {isFirstInGroup && seriesName && groupCount > 1 && (
                                <tr className="bg-muted/40">
                                  <td colSpan={4} className="px-6 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                    {seriesName} series — {groupCount} books on your list
                                  </td>
                                </tr>
                              )}
                              <tr
                                className={`hover:bg-muted/10 transition-colors ${hasMultipleFormats ? "cursor-pointer select-none" : ""}`}
                                onClick={() => hasMultipleFormats && toggleRow(idx)}
                                data-testid={`row-book-${idx}`}
                              >
                                <td className="px-6 py-4">
                                  <div className="flex items-start gap-2">
                                    {hasMultipleFormats && (
                                      <span className="mt-0.5 text-muted-foreground shrink-0">
                                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                      </span>
                                    )}
                                    <div>
                                      <div className="font-medium text-foreground mb-0.5">
                                        {result.catalogUrl ? (
                                          <a href={result.catalogUrl} target="_blank" rel="noreferrer"
                                            className="hover:underline hover:text-primary"
                                            onClick={(e) => e.stopPropagation()} data-testid={`link-catalog-${idx}`}>
                                            {result.foundTitle ?? result.title}
                                          </a>
                                        ) : (result.foundTitle ?? result.title)}
                                      </div>
                                      <div className="text-muted-foreground text-xs">{result.foundAuthor ?? result.author}</div>
                                      {result.series && (
                                        <Badge variant="secondary" className="mt-1 text-[10px] font-normal" data-testid={`badge-series-${idx}`}>
                                          {result.series}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  {result.found ? (
                                    <div className="flex flex-wrap gap-1">
                                      {result.hasLargePrint && <Badge variant="outline" className="bg-white dark:bg-black/20 text-xs">Large Print</Badge>}
                                      {result.hasPhysical && <Badge variant="outline" className="bg-white dark:bg-black/20 text-xs">Print</Badge>}
                                      {result.hasEbook && <Badge variant="outline" className="bg-white dark:bg-black/20 text-xs">Ebook</Badge>}
                                      {!result.hasPhysical && !result.hasEbook && !result.hasLargePrint && (
                                        <span className="text-muted-foreground italic text-xs">Unknown</span>
                                      )}
                                    </div>
                                  ) : (<span className="text-muted-foreground">—</span>)}
                                </td>
                                <td className="px-6 py-4">
                                  {result.found ? (
                                    <div className="space-y-1 text-xs">
                                      <div className="flex justify-between gap-4">
                                        <span className="text-muted-foreground">Beatley:</span>
                                        <span className={result.beatleyAvailable > 0 ? "font-medium text-primary" : "font-medium"}>
                                          {result.beatleyTotal > 0 ? `${result.beatleyAvailable} of ${result.beatleyTotal}` : "Not here"}
                                        </span>
                                      </div>
                                      <div className="flex justify-between gap-4">
                                        <span className="text-muted-foreground">System:</span>
                                        <span>{result.systemTotal > 0 ? `${result.systemAvailable} of ${result.systemTotal}` : "—"}</span>
                                      </div>
                                      <div className={`flex justify-between gap-4 ${result.holds > 0 ? "text-amber-600 dark:text-amber-500" : "text-muted-foreground"}`}>
                                        <span>Waiting:</span>
                                        <span data-testid={`waiting-${idx}`}>
                                          {result.holds > 0 ? `${result.holds} ${result.holds === 1 ? "person" : "people"}` : "No one"}
                                        </span>
                                      </div>
                                      {!hasMultipleFormats && result.catalogUrl && (
                                        <HoldButton
                                          catalogUrl={result.catalogUrl}
                                          onPlaceHold={onPlaceHold}
                                          testid={`button-hold-single-${idx}`}
                                        />
                                      )}
                                      {hasMultipleFormats && (
                                        <div className="text-muted-foreground italic mt-1">Click row for per-format holds</div>
                                      )}
                                    </div>
                                  ) : (<span className="text-muted-foreground">—</span>)}
                                </td>
                                <td className="px-6 py-4">{statusBadge(result)}</td>
                              </tr>
                              {isExpanded && (result.formats ?? []).length > 0 && (
                                result.formats.map((fmt, fi) => (
                                  <FormatRow key={`fmt-${idx}-${fi}`} fmt={fmt} isLast={fi === result.formats.length - 1} onPlaceHold={onPlaceHold} />
                                ))
                              )}
                            </Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
